import React from 'react';
import { useLocation } from 'react-router-dom';
import './LegalPages.css';

const AgreementPage = () => {
    const location = useLocation();
    const isEnglish = location.pathname.startsWith('/en');

    if (isEnglish) {
        return (
            <div className="legal-page-container">
                <h1>Accommodation Agreement</h1>
                <p><strong>Please note:</strong> This is a translation provided for convenience. The original Japanese version of this agreement shall be the governing text.</p>

                <h2>Article 1 (Scope of Application)</h2>
                <p>1. Accommodation Contracts and related agreements concluded between this Facility and a Guest shall be governed by these Terms and Conditions. Any matters not stipulated herein shall be governed by the laws and regulations of Japan or generally established customs.</p>
                <p>2. If the Facility agrees to a special agreement that does not contravene laws, regulations, or customs, that special agreement shall take precedence over these Terms and Conditions.</p>

                <h2>Article 2 (Application for Accommodation Contract)</h2>
                <p>1. A person applying for an Accommodation Contract with the Facility shall provide the following information:</p>
                <ol>
                    <li>(1) Name of the Guest(s)</li>
                    <li>(2) Date of stay and estimated time of arrival</li>
                    <li>(3) Accommodation fees (based, in principle, on the Basic Accommodation Fees in Appendix 1)</li>
                    <li>(4) Other information deemed necessary by the Facility</li>
                </ol>
                <p>2. If a Guest requests to extend their stay beyond the date specified in item (2) of the preceding paragraph, the Facility shall treat it as a new application for an Accommodation Contract at the time the request is made.</p>

                <h2>Article 3 (Conclusion of Accommodation Contract, etc.)</h2>
                <p>1. The Accommodation Contract shall be concluded when the Facility accepts the application described in the preceding Article. However, this shall not apply if the Facility proves that it did not grant acceptance.</p>
                <p>2. When the Accommodation Contract is concluded in accordance with the preceding paragraph, the Guest shall pay a deposit specified by the Facility, up to the amount of the Basic Accommodation Fee for the duration of the stay, by the date designated by the Facility.</p>
                <p>3. The deposit shall first be applied to the final accommodation fees to be paid by the Guest. If circumstances requiring the application of Articles 6 or 18 arise, the deposit shall be applied to the cancellation fees, followed by compensation for damages, in that order. Any remaining amount shall be refunded at the time of payment of fees as stipulated in Article 12.</p>
                <p>4. If the Guest fails to pay the deposit by the date designated by the Facility as stipulated in Paragraph 2, the Accommodation Contract shall become invalid. However, this shall apply only if the Facility has informed the Guest of this provision when specifying the payment due date for the deposit.</p>

                <h2>Article 4 (Special Agreement Not Requiring a Deposit)</h2>
                <p>1. Notwithstanding the provisions of Paragraph 2 of the preceding Article, the Facility may agree to a special arrangement that does not require the payment of a deposit after the contract has been concluded.</p>
                <p>2. If the Facility does not request payment of the deposit as stipulated in Paragraph 2 of the preceding Article, or does not specify a due date for the deposit upon accepting the application for an Accommodation Contract, it shall be treated as having agreed to the special arrangement described in the preceding paragraph.</p>

                <h2>Article 5 (Refusal to Conclude an Accommodation Contract)</h2>
                <p>The Facility may refuse to conclude an Accommodation Contract in the following cases:</p>
                <ol>
                    <li>(1) When the application for accommodation does not comply with these Terms and Conditions.</li>
                    <li>(2) When there are no available rooms due to full occupancy.</li>
                    <li>(3) When the prospective Guest is deemed likely to engage in acts that violate laws, public order, or good morals during their stay.</li>
                    <li>(4) When the prospective Guest is clearly identified as having a serious infectious disease that can be transmitted to others.</li>
                    <li>(5) When the prospective Guest is recognized as falling under any of the following categories (a) through (c):<br/>
                    (a) An anti-social force, such as an organized crime group (Boryokudan), a member of such a group, or any related parties.<br/>
                    (b) A corporation or other entity whose business activities are controlled by an anti-social force.<br/>
                    (c) A corporation with an officer who is a member of an organized crime group.</li>
                    <li>(6) When the prospective Guest engages in behavior that causes significant disturbance to other guests.</li>
                    <li>(7) When the prospective Guest is clearly identified as having a contagious disease.</li>
                    <li>(8) When violent, threatening, or unreasonably burdensome demands are made in relation to the accommodation.</li>
                    <li>(9) When the Facility is unable to provide accommodation due to natural disasters, facility malfunctions, or other unavoidable circumstances.</li>
                    <li>(10) When the situation falls under provisions stipulated in prefectural ordinances or other applicable regulations.</li>
                </ol>

                <h2>Article 6 (Guest's Right to Cancel the Contract)</h2>
                <p>1. The Guest may cancel the Accommodation Contract by notifying the Facility.</p>
                <p>2. If the Guest cancels the Accommodation Contract in whole or in part for reasons attributable to the Guest, the Facility shall charge a cancellation fee as specified separately (in Appendix 2, etc.). However, in cases where the Facility has agreed to a special arrangement as stipulated in Article 4, Paragraph 1, this shall apply only if the Facility has informed the Guest of their obligation to pay a cancellation fee when the Guest cancels the contract.</p>
                <p>3. If the Guest does not arrive by 8:00 PM on the day of their stay without prior notice (or one hour after the expected arrival time if one was specified), the Facility may deem the Accommodation Contract to have been canceled by the Guest.</p>

                <h2>Article 7 (Facility's Right to Cancel the Contract)</h2>
                <p>1. The Facility may cancel the Accommodation Contract in the following cases:</p>
                <ol>
                    <li>(1) When the Guest is deemed likely to engage in, or has engaged in, acts that violate laws, public order, or good morals during their stay.</li>
                    <li>(2) When the Guest is recognized as falling under any of the categories (a) through (c) of Article 5, Paragraph 5.</li>
                    <li>(3) When the Guest engages in behavior that causes significant disturbance to other guests.</li>
                    <li>(4) When the Guest is clearly identified as having a contagious disease.</li>
                    <li>(5) When violent, threatening, or unreasonably burdensome demands are made in relation to the accommodation.</li>
                    <li>(6) When the Facility is unable to provide accommodation due to force majeure events such as natural disasters.</li>
                    <li>(7) When the situation falls under provisions stipulated in prefectural ordinances.</li>
                    <li>(8) When the Guest smokes in non-designated areas, tampers with fire-fighting equipment, or fails to comply with other prohibitions of the Usage Rules established by the Facility (limited to those necessary for fire prevention).</li>
                </ol>
                <p>2. If the Facility cancels the Accommodation Contract based on the preceding provisions, the Guest will not be charged for accommodation services not yet received.</p>
                
                <h2>Article 8 (Accommodation Registration)</h2>
                <p>1. The Guest shall register the following information at the Facility on the day of their stay:</p>
                <ol>
                    <li>(1) Name, age, gender, address, and occupation of the Guest.</li>
                    <li>(2) For foreign nationals, their nationality, passport number, point of entry, and date of entry into Japan.</li>
                    <li>(3) Departure date and scheduled departure time.</li>
                    <li>(4) Other information deemed necessary by the Facility.</li>
                </ol>
                <p>2. If the Guest intends to pay the fees stipulated in Article 12 by means that can substitute for currency, such as traveler's checks, accommodation vouchers, or credit cards, they shall present them at the time of registration as described in the preceding paragraph.</p>

                <h2>Article 9 (Room Usage Hours)</h2>
                <p>1. The Guest may use the Facility's rooms from 3:00 PM to 10:00 AM the following day. For consecutive stays, the Guest may use the room all day, except on the arrival and departure days. However, the Facility staff may enter the room for cleaning or maintenance purposes between 10:00 AM and 3:00 PM.</p>
                <p>2. Notwithstanding the preceding paragraph, the Facility may permit room usage outside the hours specified. In such cases, the following additional fees will apply:</p>
                <ol>
                    <li>(1) For up to 2 hours of extension: 3,000 JPY per hour.</li>
                    <li>(2) For extensions exceeding 2 hours: The full room rate for one night.</li>
                </ol>

                <h2>Article 10 (Compliance with Usage Rules)</h2>
                <p>Within the Facility, the Guest shall comply with the Usage Rules established and displayed by the Facility.</p>

                <h2>Article 11 (Business Hours)</h2>
                <p>The Facility's business hours will be announced on the official website or by other means. These hours may be temporarily changed when necessary and unavoidable. In such cases, the Guest will be notified through appropriate methods.</p>

                <h2>Article 12 (Payment of Fees)</h2>
                <p>1. The breakdown of accommodation fees to be paid by the Guest is as shown in Appendix 1.</p>
                <p>2. Payment of the accommodation fees described in the preceding paragraph shall be made at the time of the Guest's departure or when requested by the Facility, either at the front desk or through online prepayment, using currency or alternative methods approved by the Facility, such as traveler's checks, accommodation vouchers, or credit cards.</p>
                <p>3. Accommodation fees shall be charged even if the Guest voluntarily chooses not to stay after the room has been made available to them.</p>

                <h2>Article 13 (Facility's Liability)</h2>
                <p>The Facility shall not be liable for any damages suffered by the Guest unless such damages are caused by the Facility's willful misconduct or negligence. If damages occur due to the Facility's fault, compensation shall be limited to the amount of the accommodation fee, unless there is willful misconduct or gross negligence on the part of the Facility.</p>

                <h2>Article 14 (Handling When Contracted Rooms Cannot Be Provided)</h2>
                <p>1. If the Facility is unable to provide the contracted room to the Guest, it shall, with the Guest's consent, arrange for alternative accommodation under the same conditions as far as possible.</p>
                <p>2. If the Facility is unable to arrange alternative accommodation despite the preceding paragraph, it shall pay the Guest a compensation fee equivalent to the cancellation fee, which shall be applied to the amount of damages. However, if the inability to provide a room is not attributable to the Facility, no compensation fee will be paid.</p>

                <h2>Article 15 (Custody of Guest's Baggage or Belongings)</h2>
                <p>1. If a Guest's baggage arrives at the Facility before their stay, the Facility shall store it with due care only if the Facility has agreed to do so in advance, and shall hand it to the Guest at check-in.</p>
                <p>2. If a Guest's baggage or belongings are left at the Facility after check-out, and the owner is identified, the Facility shall contact the owner and ask for instructions. However, if no instructions are given by the owner or if the owner cannot be identified, the items will be stored for two months including the day they were found.</p>
                <p>3. The Facility's liability for the custody of the Guest's baggage or belongings in the cases of the preceding two paragraphs shall be in accordance with the provisions of the preceding Article.</p>

                <h2>Article 16 (Parking Liability)</h2>
                <p>When a Guest uses the Facility's parking area, the Facility merely provides a space and does not assume responsibility for the management of the vehicle, regardless of whether the vehicle key has been entrusted to the Facility. However, if damage is caused through the willful misconduct or negligence of the Facility in managing the parking area, the Facility shall be liable for compensation.</p>

                <div className="appendix-section">
                    <h3>Appendix 1: Breakdown of Accommodation Fees (Related to Article 2, Paragraph 1 and Article 12, Paragraph 1)</h3>
                    <h4>Accommodation Charges</h4>
                    <ul>
                        <li>① Basic Accommodation Fee (Room Rate)</li>
                    </ul>
                    <h4>Additional Charges</h4>
                    <ul>
                        <li>② Additional Service Fees (e.g., late check-out, extra cleaning)</li>
                    </ul>
                    <h4>Taxes</h4>
                    <ul>
                        <li>(a) Consumption Tax</li>
                        <li>(b) Accommodation Tax</li>
                    </ul>
                    <h4>Remarks</h4>
                    <p>The Basic Accommodation Fee is based on the price list of the reservation system on the Facility's official website.</p>
                    <p>No accommodation fee is charged for children under 5 years of age who do not require bedding.</p>
                    <p>Guests aged 6 and over will be charged the adult rate, regardless of whether bedding is required.</p>
                </div>

                <p className="date-established">Enacted: September 16, 2024</p>
            </div>
        );
    }

    return (
        <div className="legal-page-container">
            {/* ... ここに日本語版の全文が入ります ... */}
            <h1>宿泊約款</h1>
            
            <h2>第１条 適用範囲</h2>
            <p>当宿泊施設が宿泊客との間で締結する宿泊契約及びこれに関連する契約は、この約款の定めるところによるものとし、この約款に定めのない事項については、法令又は一般に確立された慣習によるものとします。</p>
            <p>当宿泊施設が、法令及び慣習に反しない範囲で特約に応じたときは、前項の規定にかかわらず、その特約が優先するものとします。</p>
            
            <h2>第２条 宿泊契約の申込み</h2>
            <p>当宿泊施設に宿泊契約の申込みをしようとする者は、次の事項を当宿泊施設に申し出ていただきます。</p>
            <ol>
                <li>(1) 宿泊者名</li>
                <li>(2) 宿泊日及び到着予定時刻</li>
                <li>(3) 宿泊料金(原則として別表第１の基本宿泊料による。)</li>
                <li>(4) その他当宿泊施設が必要と認める事項</li>
            </ol>
            <p>宿泊客が、宿泊中に前項第２号の宿泊日を超えて宿泊の継続を申し入れた場合、当宿泊施設は、その申し出がなされた時点で新たな宿泊契約の申し込みがあったものとして処理します。</p>

            <h2>第３条 宿泊契約の成立等</h2>
            <p>宿泊契約は、当宿泊施設が前条の申し込みを承諾したときに成立するものとします。ただし、当宿泊施設が承諾をしなかったことを証明したときは、この限りではありません。</p>
            <p>前項の規定により宿泊契約が成立したときは、宿泊期間の基本宿泊料を限度として当宿泊施設が定める申込金を、当宿泊施設が指定する日までに、お支払いいただきます。</p>
            <p>申込金は、まず、宿泊客が最終的に支払うべき宿泊料金に充当し、第6条及び第18条の規定を適用する事態が生じたときは、違約金に次いで賠償金の順序で充当し、残額があれば、第12条の規定による料金の支払いの際に返還します。</p>
            <p>第2項の申込金を同項の規定により当宿泊施設が指定した日までにお支払いいただけない場合は、宿泊契約はその効力を失うものとします。ただし、申込金の支払期日を指定するに当たり、当宿泊施設がその旨を宿泊客に告知した場合に限ります。</p>

            <h2>第４条 申込金の支払いを要しないこととする特約</h2>
            <p>前条第2項の規定にかかわらず、当宿泊施設は、契約の成立後同項の申込金の支払いを要しないこととする特約に応じることがあります。</p>
            <p>宿泊契約の申し込みを承諾するに当たり、当宿泊施設が前条第2項の申込金の支払いを求めなかった場合及び当該申込金の支払期日を指定しなかった場合は、前項の特約に応じたものとして取り扱います。</p>

            <h2>第５条 宿泊契約締結の拒否</h2>
            <p>当宿泊施設は、次の場合には、宿泊契約の締結に応じないことがあります。</p>
            <ol>
                <li>(1) 宿泊の申し込みが、この約款によらないとき。</li>
                <li>(2) 満室（員）により客室の余裕がないとき。</li>
                <li>(3) 宿泊しようとする者が、宿泊に関し、法令の規定、公の秩序若しくは善良の風俗に反する行為をするおそれがあると認められるとき。</li>
                <li>(4) 宿泊しようとする者が宿泊を通じて人から人に感染し重篤な症状を引き起こすおそれのある感染症にかかっていると明らかに認められるとき。</li>
                <li>(5) 宿泊しようとする者が、次のイからハに該当すると認められるとき。<br/>
                イ 暴力団員による不当な行為の防止等に関する法律（平成３年法律第７７号）第２条第２号に規定する暴力団（以下「暴力団」という。）、同条第２条第６号に規定する暴力団員（以下「暴力団員」という。）、暴力団準構成員又は暴力団関係者その他の反社会的勢力<br/>
                ロ 暴力団又は暴力団員が事業活動を支配する法人その他の団体であるとき<br/>
                ハ 法人でその役員のうちに暴力団員に該当する者があるもの</li>
                <li>(6) 宿泊しようとする者が、他の宿泊客に著しい迷惑を及ぼす言動をしたとき。</li>
                <li>(7) 宿泊しようとする者が、伝染病者であると明らかに認められるとき。</li>
                <li>(8) 宿泊に関し暴力的要求行為が行われ、又は合理的な範囲を超える負担を求められたとき。</li>
                <li>(9) 天災、施設の故障、その他やむを得ない事由により宿泊させることができないとき。</li>
                <li>(10) 都道府県条例等の規定する場合に該当するとき。</li>
            </ol>

            <h2>第６条 宿泊客の契約解除権</h2>
            <p>宿泊客は、当宿泊施設に申し出て、宿泊契約を解除することができます。</p>
            <p>当宿泊施設は、宿泊客がその責めに帰すべき事由により宿泊契約の全部又は一部を解除した場合(第3条第2項の規定により当宿泊施設が申込金の支払期日を指定してその支払いを求めた場合であって、その支払いより前に宿泊客が宿泊契約を解除したときを除きます。)は、別表第2に掲げるところにより、違約金を申し受けます。ただし、当宿泊施設が第4条第1項の特約に応じた場合にあっては、その特約に応じるに当たって、宿泊客が宿泊契約を解除したときの違約金支払義務について、当宿泊施設が宿泊客に告知したときに限ります。</p>
            <p>当宿泊施設は、宿泊客が連絡をしないで宿泊日当日の午後8時(あらかじめ到着予定時刻が明示されている場合は、その時刻を1時間経過した時刻)になっても到着しないときは、その宿泊契約は宿泊客により解除されたものとみなし処理することがあります。</p>

            <h2>第７条 当宿泊施設の契約解除権</h2>
            <p>当宿泊施設は、次の場合に宿泊契約を解除することがあります。</p>
            <ol>
                <li>(1) 宿泊客が宿泊に関し、法令の規定、公の秩序若しくは善良の風俗に反する行為をするおそれがあると認められるとき、又は同行為をしたと認められるとき。</li>
                <li>(2) 宿泊客が次のイからハに該当すると認められるとき。<br/>
                イ 暴力団、暴力団員、暴力団準構成員又は暴力団関係者その他の反社会的勢力<br/>
                ロ 暴力団又は暴力団員が事業活動を支配する法人その他の団体であるとき<br/>
                ハ 法人でその役員のうちに暴力団員に該当する者があるもの</li>
                <li>(3) 宿泊客が他の宿泊客に著しい迷惑を及ぼす言動をしたとき。</li>
                <li>(4) 宿泊客が伝染病者であると明らかに認められるとき。</li>
                <li>(5) 宿泊に関し暴力的要求行為が行われ、又は合理的な範囲を超える負担を求められたとき。</li>
                <li>(6) 天災等不可抗力に起因する事ゆえより宿泊させることができないとき。</li>
                <li>(7) 都道府県条例の規定する場合に該当するとき。</li>
                <li>(8) 定められた場所以外での喫煙、消防用設備等に対するいたずら、その他当宿泊施設が定める利用規則の禁止事項（火災予防上必要なものに限る。）に従わないとき。</li>
            </ol>
            <p>当宿泊施設が前項の規定に基づいて宿泊契約を解除したときは、宿泊客がいまだ提供を受けていない宿泊サービス等の料金はいただきません。</p>
            
            <h2>第８条 宿泊の登録</h2>
            <p>宿泊客は、宿泊日当日、当宿泊施設において、次の事項を登録していただきます。</p>
            <ol>
                <li>(1) 宿泊客の氏名、年齢、性別、住所及び職業</li>
                <li>(2) 外国人にあっては、国籍、旅券番号、入国地及び入国年月日</li>
                <li>(3) 出発日及び出発予定時刻</li>
                <li>(4) その他当宿泊施設が必要と認める事項</li>
            </ol>
            <p>宿泊客が第12条の料金の支払いを、旅行小切手、宿泊券、クレジットカード等通貨に代わり得る方法により行おうとするときは、あらかじめ、前項の登録時にそれらを呈示していただきます。</p>

            <h2>第９条 客室の使用時間</h2>
            <p>宿泊客が当宿泊施設の客室を使用できる時間は、午後3時から翌朝10時までとします。ただし、連続して宿泊する場合においては、到着日及び出発日を除き、終日使用することができます。ただし、午前10時から午後3時に客室清掃又は客室管理のため、入室させていただくことがあります。</p>
            <p>当宿泊施設は、前項の規定にかかわらず、同項に定める時間外の客室の使用に応じることがあります。この場合には次に掲げる追加料金を申し受けます。</p>
            <ol>
                <li>(1) 超過2時間までは、1時間ごとに3,000円</li>
                <li>(2) 超過2時間以上は、室料金の全額</li>
            </ol>

            <h2>第１０条 利用規則の遵守</h2>
            <p>宿泊客は、当宿泊施設内においては、当宿泊施設が定めて館内に掲示した利用規則に従っていただきます。</p>

            <h2>第１１条 営業時間</h2>
            <p>当宿泊施設の営業時間はホームページ等で御案内いたします。必要やむを得ない場合には臨時に変更することがあります。その場合には、適当な方法をもってお知らせします。</p>

            <h2>第１２条 料金の支払い</h2>
            <p>宿泊者が支払うべき宿泊料金等の内訳は、別表第１に掲げるところによります。</p>
            <p>前項の宿泊料金等の支払いは、通貨又は当宿泊施設が認めた旅行小切手、宿泊券、クレジットカード等これに代わり得る方法により、宿泊客の出発の際又は当宿泊施設が請求した時、事前オンライン決済もしくはフロントにおいて行っていただきます。</p>
            <p>当宿泊施設が宿泊客に客室を提供し、使用が可能になったのち、宿泊客が任意に宿泊しなかった場合においても、宿泊料金は申し受けます。</p>

            <h2>第１３条 当宿泊施設の責任</h2>
            <p>当宿泊施設は、当宿泊施設の責めに帰すべき事由がない限り、宿泊客に損害が生じてもその責任は負いかねます。当宿泊施設の過失により宿泊客に損害が生じたときは、宿泊料金を限度としてその損害を賠償します。ただし、当宿泊施設に故意又は重大な過失があるときは、この限りではありません。</p>

            <h2>第１４条 契約した客室の提供ができないときの取扱い</h2>
            <p>当宿泊施設は、宿泊客に契約した客室を提供できないときは、宿泊客の了解を得て、できる限り同一の条件による他の宿泊施設をあっ旋するものとします。</p>
            <p>当宿泊施設は、前項の規定にかかわらず他の宿泊施設のあっ旋ができないときは、違約金相当額の補償料を宿泊客に支払い、その補償料は損害賠償額に充当します。ただし、客室が提供できないことについて、当宿泊施設の責めに帰すべき事由がないときは、補償料を支払いません。</p>

            <h2>第１５条 宿泊客の手荷物又は携帯品の保管</h2>
            <p>宿泊客の手荷物が、宿泊に先立って当宿泊施設に到着した場合は、その到着前に当宿泊施設が了解したときに限って責任をもって保管し、宿泊客がチェックインする際お渡しします。</p>
            <p>宿泊客がチェックアウトしたのち、宿泊客の手荷物又は携帯品が当宿泊施設に置き忘れられていた場合において、その所有者が判明したときは、当宿泊施設は、当該所有者に連絡をするとともにその指示を求めるものとします。ただし、所有者の指示がない場合又は所有者が判明しないときは、発見日を含め２か月間保管します。</p>
            <p>前2項の場合における宿泊客の手荷物又は携帯品の保管についての当宿泊施設の責任は、第1項の場合にあっては前条第1項の規定に、前項の場合にあっては同条第2項の規定に準じるものとします。</p>

            <h2>第１６条 駐車の責任</h2>
            <p>宿泊客が当宿泊施設の駐車場をご利用になる場合、車両のキーの寄託の如何にかかわらず、当宿泊施設は場所をお貸しするものであって、車両の管理責任まで負うものではありません。ただし、駐車場の管理に当たり、当宿泊施設の故意又は過失によって損害を与えたときは、その賠償の責めに任じます。</p>
            
            <div className="appendix-section">
                <h3>別表第１ 宿泊料金等の内訳 (第2条第1項及び第12条第1項関係)</h3>
                <h4>宿泊料金</h4>
                <ul>
                    <li>① 基本宿泊料（室料）</li>
                </ul>
                <h4>追加料金</h4>
                <ul>
                    <li>② 追加サービス料（滞在時間延長サービス、追加清掃など）</li>
                </ul>
                <h4>税金</h4>
                <ul>
                    <li>イ 消費税</li>
                    <li>ロ 宿泊税</li>
                </ul>
                <h4>備考</h4>
                <p>基本宿泊料は当宿泊施設の公式ホームページに掲示する自社サイト予約システムの料金表によります。</p>
                <p>寝具を提供しない5歳未満の幼児については、宿泊料金をいただきません。</p>
                <p>6歳以上のお客様については、寝具の有無にかかわらず大人料金となります。</p>
            </div>

            <p className="date-established">令和6年9月16日 制定</p>
        </div>
    );
};
export default AgreementPage;